import { AUTH_STATUS, useAuthStore } from "@/auth"
import type { AxiosError } from "axios"

import {
  createBeHttpClient,
  registerHttp,
  setBasePathPrefix,
} from "@be-miniapp/api"
import { ACCESS_TOKEN_KEY, API_BASE_URL } from "@be-miniapp/app-configs"
import { instrumentHttpClient, log } from "@be-miniapp/logging"
import {
  resolveHttpRequestPath,
  withoutDefaultLocationForMapRequest,
} from "@be-miniapp/shared/utils/location-policy"

import { useClientInfo } from "@/store/client-info"
import { usePermissionValidatorStore } from "@/store/permission-validator"

const httpClient = createBeHttpClient({
  baseURL: API_BASE_URL as string,
  timeoutMs: 20000,
  auth: { mode: "authorization_bearer" },
  getToken: async () => {
    const storeToken = useClientInfo.getState().additionals.access_token
    const latestToken =
      storeToken ?? localStorage?.getItem(ACCESS_TOKEN_KEY) ?? null
    return typeof latestToken === "string" ? latestToken : null
  },
  getLocale: () => useClientInfo.getState().additionals.locale,
  getAdditionals: (config) =>
    withoutDefaultLocationForMapRequest(
      useClientInfo.getState().additionals as unknown as Record<
        string,
        unknown
      >,
      resolveHttpRequestPath(config),
      useClientInfo.getState().locationSource
    ),
  bodyAccessTokenBehavior: "strip",
})

instrumentHttpClient(httpClient, { clientName: "appview" })

httpClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError<unknown>) => {
    if (error.response?.status === 403) {
      const permissionStore = usePermissionValidatorStore.getState()
      if (!permissionStore.shouldIgnoreNeedVerification()) {
        permissionStore.updateState("need-verification")
      }
    }

    if (error.response?.status === 401) {
      if (useAuthStore.getState().status === AUTH_STATUS.AUTHENTICATED) {
        usePermissionValidatorStore.getState().updateState("unauthorized")
      }
    }

    if ((error.response?.status ?? 0) >= 400) {
      if (error.config?.url?.includes("be-trip")) {
        return Promise.reject(error)
      }

      log({
        level: "warning",
        message: "Server error",
        data: {
          response: error.response?.data,
          status: error.response?.status,
          request: error.request,
        },
        stack: error.stack,
        source: "axios",
      })
    }
    return Promise.reject(error)
  }
)

export { httpClient }

// Register shared API client and base path once
registerHttp(httpClient)
setBasePathPrefix("api/v1/be-web-gateway/gw")
